import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SECTIONS = [['schedule', 'Schedule (cleans and assignments)'], ['walkthroughs', 'Walk-Thrus'], ['needs_review', 'Needs Review queue'], ['tasks', 'Tasks due'], ['leads', 'New leads'], ['inquiries', 'New inquiries']];
const Choice = ({ name, value, options, onChange }) => (
  <div className="flex flex-wrap gap-1.5" role="radiogroup">
    {options.map(([k, l]) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`ai-pref-${name}-${k}`}
        className={`px-3.5 py-2 rounded-[5px] text-[13px] border ${value === k ? 'bg-[#252a20] text-white border-[#252a20]' : 'border-[#dcded2] text-[#5f6455] hover:bg-[#ebe8df]'}`}>{l}</button>
    ))}
  </div>
);
const Row = ({ title, note, children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-3 py-5 border-b border-[#ecebe4] last:border-0">
    <div><p className="text-sm font-semibold text-[#20211d]">{title}</p>{note && <p className="text-xs text-[#77776e] mt-1">{note}</p>}</div>
    <div>{children}</div>
  </div>
);

export default function AIPreferences({ headers }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API_URL}/api/admin/briefing/preferences`, { headers }).then((r) => setP(r.data)).catch(() => setP(false)); }, [headers]);
  if (p === null) return <Loader2 className="h-5 w-5 animate-spin text-[#f36a19]" />;
  if (p === false) return <p className="text-sm text-[#a4471b]">Preferences could not be loaded.</p>;
  const set = (k) => (v) => setP((x) => ({ ...x, [k]: v }));
  const toggle = (k) => setP((x) => ({ ...x, sections: x.sections.includes(k) ? x.sections.filter((s) => s !== k) : [...x.sections, k] }));
  const save = async (e) => {
    e.preventDefault();
    if (!p.sections.length) return toast.error('Keep at least one briefing section.');
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/api/admin/briefing/preferences`, { briefing_length: p.briefing_length, briefing_tone: p.briefing_tone, sections: p.sections, reply_style: p.reply_style, reply_signoff: p.reply_signoff || '' }, { headers });
      setP(r.data); toast.success('Preferences saved');
    } catch (err) { toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not save preferences'); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="rounded-[10px] bg-white border border-[#e7e4db] px-6 py-2" data-testid="ai-preferences">
      <Row title="Briefing length" note="How much the daily briefing covers.">
        <Choice name="length" value={p.briefing_length} onChange={set('briefing_length')} options={[['short', 'Short · 2 points'], ['standard', 'Standard · 4 points'], ['detailed', 'Detailed · 6 points']]} />
      </Row>
      <Row title="Briefing tone">
        <Choice name="tone" value={p.briefing_tone} onChange={set('briefing_tone')} options={[['plain', 'Plain'], ['friendly', 'Friendly'], ['direct', 'Direct']]} />
      </Row>
      <Row title="Sections in the briefing" note="Claude only sees the records you include here.">
        <div className="grid sm:grid-cols-2 gap-2">
          {SECTIONS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm text-[#20211d]"><input type="checkbox" checked={p.sections.includes(k)} onChange={() => toggle(k)} className="accent-[#f36a19] h-4 w-4" data-testid={`ai-pref-section-${k}`} />{l}</label>
          ))}
        </div>
      </Row>
      <Row title="Reply draft style" note="Used by “Draft with AI” in Inbox conversations. Drafts are never sent automatically.">
        <Choice name="reply" value={p.reply_style} onChange={set('reply_style')} options={[['warm', 'Warm'], ['concise', 'Concise'], ['formal', 'Formal']]} />
        <label className="block text-xs text-[#77776e] mt-4 mb-1">Sign-off (optional)</label>
        <input value={p.reply_signoff || ''} onChange={(e) => set('reply_signoff')(e.target.value)} maxLength={80} className="h-10 w-full max-w-sm rounded-md border border-[#dcded2] px-3 text-sm" data-testid="ai-pref-signoff" />
      </Row>
      <div className="flex flex-wrap items-center gap-4 py-5">
        <button type="submit" disabled={saving} className="px-4 py-2.5 rounded-[5px] bg-[#f36a19] text-[#171912] text-sm font-bold inline-flex items-center gap-2" data-testid="ai-pref-save">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save preferences</button>
        <p className="text-xs text-[#77776e]" data-testid="ai-pref-meta">{p.updated_at ? `Last saved ${new Date(p.updated_at).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} PT by ${p.updated_by_name || 'an admin'}. ` : ''}Briefing changes apply to the next briefing; today’s can be refreshed from the Daily Briefing card.</p>
      </div>
    </form>
  );
}
